import { RequestOptions } from './baseService';

export interface CVClassificationResult {
  status: string;
  message: string;
  timestamp: string;
  trigger: string;
  processed_records?: number;
  input_records?: number;
}

export class CVClassificationService {
  private readonly functionUrl = 'https://func-score-cvportal-francent-test.azurewebsites.net/api/cv-classification';

  /**
   * Trigger CV classification function
   * This is a fire-and-forget operation - we don't wait for the result
   */
  async triggerCVClassification(options: RequestOptions = {}): Promise<void> {
    try {
      // Fire and forget - don't wait for response
      fetch(this.functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          trigger: 'manual',
          timestamp: new Date().toISOString()
        })
      }).catch(error => {
        // Log error but don't throw - this is fire and forget
        console.warn('CV Classification function call failed (fire-and-forget):', error);
      });

      // Return immediately
      return Promise.resolve();
    } catch (error) {
      console.error('Error triggering CV classification:', error);
      throw error;
    }
  }

  /**
   * Trigger CV classification function and wait for result
   * Use this only if you need to wait for the response (not recommended for long-running operations)
   */
  async triggerCVClassificationWithResponse(options: RequestOptions = {}): Promise<CVClassificationResult> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), options.timeout || 300000); // 5 minute timeout

      const response = await fetch(this.functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          trigger: 'manual',
          timestamp: new Date().toISOString()
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('CV Classification function timed out');
      }
      console.error('Error triggering CV classification with response:', error);
      throw error;
    }
  }
} 